import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useState
} from "react";

import { useIntl } from "react-intl";

import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";
import { ProductKey } from "@/components/signup/utils";
import {
    JobName,
    useBxpWebStargateClient,
    type ActivateProductCrossflowResponse,
    type WaitForActivateProductCrossflowResponse
} from "@/libraries/bxp-web-stargate-client/src";

import { useCloudStatus } from "../../../hooks/use-cloud-status";

import { getProjectDetails } from "./utils";

interface ProvisioningApiContextType {
    activateProductCrossflow: (
        productKey: ProductKey
    ) => Promise<ActivateProductCrossflowResponse>;
    waitForProvisioning: (
        productKey: ProductKey
    ) => Promise<WaitForActivateProductCrossflowResponse>;
    projectKey: string;
}

const ProvisioningApiContext = createContext<
    ProvisioningApiContextType | undefined
>(undefined);

export const ProvisioningApiProvider = ({ children }: PropsWithChildren) => {
    const stargateClient = useBxpWebStargateClient();
    const { cloudId } = useCloudStatus();
    const { templateId } = useSearchParameters();
    const { formatMessage } = useIntl();
    const [projectKey, setProjectKey] = useState<string>("");

    const activateProductCrossflow = useCallback(
        async (productKey: ProductKey) => {
            const projectDetails = getProjectDetails(productKey);
            if (!projectDetails) {
                throw new Error(
                    `Project details not found for product key: ${productKey}`
                );
            }
            const { projectKey, defaultProjectName } = projectDetails;
            setProjectKey(projectKey);
            return stargateClient.activateProductCrossflow({
                cloudId,
                productKey,
                jobName: JobName.WAC_SIGNUP,
                context: {
                    sourceComponent: "wacTemplateChoiceScreen",
                    sourceContext: "templateCard",
                    originProduct: "wac",
                    templateKey: templateId,
                    projectKey,
                    projectName: formatMessage(defaultProjectName),
                    templateVersion: "roadmap_v1"
                }
            });
        },
        [stargateClient, cloudId, templateId, formatMessage]
    );

    const waitForProvisioning = useCallback(
        (productKey: ProductKey) =>
            stargateClient.waitForActivateProductCrossflow({
                cloudId,
                productKey,
                jobName: JobName.WAC_SIGNUP
            }),
        [cloudId, stargateClient]
    );

    return (
        <ProvisioningApiContext.Provider
            value={{
                activateProductCrossflow,
                waitForProvisioning,
                projectKey
            }}
        >
            {children}
        </ProvisioningApiContext.Provider>
    );
};

export const useProvisioningApi = () => {
    const context = useContext(ProvisioningApiContext);
    if (!context) {
        throw new Error(
            "useProvisioningApi must be used within a ProvisioningApiProvider"
        );
    }
    return context;
};
